import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";
import { DemoProvider, useDemo } from "@/components/public/DemoDialog";

const navItems = [
  { to: "/#platform", label: "Platform", id: "platform" },
  { to: "/#workflow", label: "How It Works", id: "workflow" },
  { to: "/#solutions", label: "Solutions", id: "solutions" },
  { to: "/pricing", label: "Pricing", id: "pricing" },
  { to: "/about", label: "About", id: "about" },
];

const footer = {
  Product: [
    { to: "/#platform", label: "AI Telephonic Screening" },
    { to: "/#platform", label: "AI Video Interview" },
    { to: "/#platform", label: "Live Coding Sandbox" },
    { to: "/#platform", label: "AI Scorecards & Reports" },
    { to: "/#platform", label: "Analytics & Reporting" },
  ],
  Solutions: [
    { to: "/#solutions", label: "IT Consultancies & Staffing" },
    { to: "/#solutions", label: "Tech & Product Companies" },
    { to: "/#solutions", label: "BPO & Contact Centres" },
    { to: "/#solutions", label: "Campus & Volume Hiring" },
    { to: "/#solutions", label: "RPO & Enterprise HR" },
  ],
  Company: [
    { to: "/about", label: "About" },
    { to: "/pricing", label: "Pricing" },
    { to: "/careers", label: "Careers" },
    { to: "/blog", label: "Blog" },
  ],
  Legal: [
    { to: "/legal/privacy", label: "Privacy Policy" },
    { to: "/legal/terms", label: "Terms of Service" },
    { to: "/legal/cookies", label: "Cookie Policy" },
    { to: "/legal/gdpr", label: "GDPR" },
  ],
};

function NavCtas({ mobile, onAction }) {
  const { openDemo: open } = useDemo();
  const openDemo = () => { onAction?.(); open(); };
  const sfx = mobile ? "-mobile" : "";
  return (
    <div className={mobile ? "flex flex-col gap-2" : "hidden lg:flex items-center gap-2.5"}>
      <Link to="/login" data-testid={`nav-login${sfx}`} className="px-3 py-2 text-sm text-slate-700 hover:text-slate-900 font-medium">Sign in</Link>
      <button onClick={openDemo} data-testid={`nav-btn-book-demo${sfx}`} className="px-4 py-2 rounded-full border border-slate-300 hover:border-slate-900 text-sm font-semibold text-slate-900 transition-colors">Book a Demo</button>
      <Link to="/register" data-testid={`nav-btn-start-trial${sfx}`} className="px-4 py-2 rounded-full bg-slate-900 hover:bg-indigo-600 text-white text-sm font-semibold inline-flex items-center justify-center gap-1.5 transition-colors">
        Start Free Trial <ArrowRight className="w-3.5 h-3.5" />
      </Link>
    </div>
  );
}

export function PublicNav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();
  useEffect(() => { setOpen(false); }, [loc.pathname, loc.hash]);
  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 8);
    h(); window.addEventListener("scroll", h, { passive: true });
    return () => window.removeEventListener("scroll", h);
  }, []);
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  return (
    <header className={`sticky top-0 z-40 transition-colors duration-300 bg-white/85 backdrop-blur-xl border-b ${scrolled || open ? "border-slate-200/70" : "border-transparent"}`}>
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center shrink-0" data-testid="public-brand">
          <img src="/recruitsift-logo.png" alt="RecruitSift" className="h-7 sm:h-8 w-auto" />
        </Link>
        <div className="hidden lg:flex items-center gap-1">
          {navItems.map((n) => (
            <Link key={n.label} to={n.to} data-testid={`nav-link-${n.id}`} className="px-3 py-2 rounded-full text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-100/80 font-medium transition-colors">{n.label}</Link>
          ))}
        </div>
        <NavCtas />
        <button className="lg:hidden p-2 -mr-1 rounded-lg hover:bg-slate-100 text-slate-700" onClick={() => setOpen(!open)}
          aria-label={open ? "Close menu" : "Open menu"} data-testid="public-nav-toggle">
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </nav>
      {open && (
        <div className="lg:hidden border-t border-slate-100 bg-white h-[calc(100dvh-4rem)] overflow-y-auto" data-testid="public-nav-mobile">
          <div className="px-4 py-4 flex flex-col gap-1">
            {navItems.map((n) => (
              <Link key={n.label} to={n.to} onClick={() => setOpen(false)} data-testid={`nav-link-${n.id}-mobile`} className="px-3 py-3 rounded-xl text-slate-800 hover:bg-slate-50 font-medium text-base">{n.label}</Link>
            ))}
            <div className="h-px bg-slate-100 my-3" />
            <NavCtas mobile onAction={() => setOpen(false)} />
          </div>
        </div>
      )}
    </header>
  );
}

export function PublicFooter() {
  const { openDemo } = useDemo();
  return (
    <footer className="bg-[#050810] text-slate-400 border-t border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-16">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 sm:gap-10">
          <div className="col-span-2 md:col-span-3 lg:col-span-2">
            <Link to="/" className="inline-flex items-center rounded-xl bg-white px-3.5 py-2.5 shadow-sm" data-testid="footer-brand">
              <img src="/recruitsift-logo.png" alt="RecruitSift" className="h-7 w-auto" />
            </Link>
            <p className="text-sm mt-4 leading-relaxed max-w-xs">AI interviewers that screen, assess and score talent at any volume — so your recruiters spend time closing, not calling.</p>
            <button onClick={openDemo} data-testid="footer-book-demo" className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-white hover:text-indigo-300 transition-colors">Book a demo <ArrowRight className="w-4 h-4" /></button>
          </div>
          {Object.entries(footer).map(([col, links]) => (
            <div key={col}>
              <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 mb-4 font-semibold font-mono">{col}</div>
              <ul className="space-y-2.5">
                {links.map((l) => (
                  <li key={l.label}><Link to={l.to} className="text-sm text-slate-300 hover:text-white transition-colors">{l.label}</Link></li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="border-t border-white/10 mt-12 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-center sm:text-left">
          <div>© {new Date().getFullYear()} RecruitSift. All rights reserved.</div>
          <div>Built for high-volume hiring teams in every industry.</div>
        </div>
      </div>
    </footer>
  );
}

function useHashScroll() {
  const { pathname, hash, key } = useLocation();
  useEffect(() => {
    if (!hash) { window.scrollTo(0, 0); return; }
    let n = 0;
    const t = setInterval(() => {
      const el = document.getElementById(hash.slice(1));
      if (el || ++n > 20) { clearInterval(t); el?.scrollIntoView({ behavior: "smooth", block: "start" }); }
    }, 50);
    return () => clearInterval(t);
  }, [pathname, hash, key]);
}

export function PublicLayout({ children }) {
  useHashScroll();
  return (
    <DemoProvider>
      <div className="min-h-screen flex flex-col bg-white text-slate-900 font-[Inter,system-ui,sans-serif] overflow-x-clip">
        <PublicNav />
        <main className="flex-1">{children}</main>
        <PublicFooter />
      </div>
    </DemoProvider>
  );
}
